import { Button, ButtonGroup, ConfirmDialog, Field, IMessageProps, TextInput } from 'Components/Core';
import { NamePrefixModifier, withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { ListFieldSection, SectionGroup } from 'Components/Layout';
import { hasValue } from 'Lib/Any';
import { Guid } from 'Lib/Guid';
import { joinPaths } from 'Lib/Object/Path/shared';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import UploadImageDialog from './UploadImageDialog';
import WorksheetImagePreview from './WorksheetImagePreview';
import './WorksheetImagesAndRemarksSection.scss';
import { IWorksheetImageSectionGroupProps } from './types';

/**
 * The group of sections for manipulating a single worksheet image attachment, as determined by index.
 *
 * Assumes this is prefixed to the path of the image collection.
 */
let WorksheetImageSectionGroup: React.FC<IWorksheetImageSectionGroupProps> = ({
  createWorksheetSelector,
  removeArrayEntry,
  worksheetId,
  prefixName,
  index,
}) => {
  const [showUploadImageDialog, setShowUploadImageDialog] = useState(false);
  const isWorksheetSaving = useSelector((s) => s.worksheetEditor.status.saving);

  const isUploaded: boolean | undefined = useSelector(
    createWorksheetSelector!(joinPaths(index?.toString(), 'IsUploaded'))
  );
  const imageId: Guid | undefined = useSelector(createWorksheetSelector!(joinPaths(index?.toString(), 'ImageId')));
  const modifiedDate: string | undefined = useSelector(
    createWorksheetSelector!(joinPaths(index?.toString(), 'ModifiedDate'))
  );
  const caption: string | undefined = useSelector(createWorksheetSelector!(joinPaths(index?.toString(), 'Caption')));

  if (!hasValue(imageId) || !hasValue(index)) return null;

  const removeImage = () => {
    removeArrayEntry!({
      name: prefixName!(''),
      index: index!,
      data: undefined,
      sync: true,
    });
  };

  let imageMessages: IMessageProps[] = [];

  if (!isUploaded) {
    imageMessages.push({
      severity: 'info',
      message: 'No file has been uploaded for this image. Use the "Change Image" button to upload a new image.',
    });
  }

  return (
    <SectionGroup heading={`Image ${index! + 1}`}>
      {isUploaded && (
        <ListFieldSection single>
          <WorksheetImagePreview imageId={imageId!} modifiedDate={modifiedDate} caption={caption} />
        </ListFieldSection>
      )}
      <ListFieldSection messages={imageMessages}>
        <NamePrefixModifier appendNamePrefix={index!.toString()}>
          <Field input={<TextInput />} name="Caption" label="Caption" maxLength={255} />
          <Field input={<TextInput />} name="Description" label="Description" />
        </NamePrefixModifier>
      </ListFieldSection>
      <ListFieldSection>
        <ButtonGroup>
          <Button
            onClick={() => {
              setShowUploadImageDialog(true);
            }}
            disabled={isWorksheetSaving}
          >
            Change Image
          </Button>

          <ConfirmDialog
            confirm
            confirmText={`Removing an image will delete all data present in that subsection. This action cannot be undone. Continue?`}
            confirmButtonText="Delete Image"
            confirmTitle="Delete Image"
          >
            <Button onClick={() => removeImage()} disabled={isWorksheetSaving} error>
              Remove Image
            </Button>
          </ConfirmDialog>
        </ButtonGroup>
      </ListFieldSection>
      <UploadImageDialog
        visible={showUploadImageDialog}
        worksheetId={worksheetId!}
        imageId={imageId!}
        onClose={() => {
          setShowUploadImageDialog(false);
        }}
      />
    </SectionGroup>
  );
};

WorksheetImageSectionGroup = withInjectedProps(WorksheetImageSectionGroup);
export { WorksheetImageSectionGroup };
